import React from "react";
import { S } from "../site.js";

const {
  useEffect: useEffect_H8CX,
  useRef: useRef_H8CX,
  useState: useState_H8CX,
} = React;

const H8_CX_JOURNEY = [
  { id: "attract", label: "Attract", body: "Make the right information easier to find and understand." },
  { id: "understand", label: "Understand", body: "Use guided questions to clarify what a visitor needs." },
  { id: "respond", label: "Respond", body: "Offer useful, approved information at the right moment." },
  { id: "handover", label: "Human handover", body: "Bring a person in when judgment or follow-up matters." },
  { id: "learn", label: "Learn", body: "Review real questions and improve the experience over time." },
];

function useH8CXLifecycle(sectionRef) {
  const [state, setState] = useState_H8CX({ intersecting: false, visible: true, reduced: false });
  useEffect_H8CX(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let intersecting = false;
    let initialFrame = null;

    const sync = () => setState({
      intersecting,
      visible: document.visibilityState === "visible",
      reduced: motionQuery.matches,
    });
    const measure = () => {
      const rect = section.getBoundingClientRect();
      const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0));
      intersecting = rect.height > 0 && visibleHeight / rect.height >= 0.25;
      sync();
    };
    const observer = new IntersectionObserver(([entry]) => {
      intersecting = entry.isIntersecting && entry.intersectionRatio >= 0.25;
      sync();
    }, { threshold: [0, 0.25, 1] });

    observer.observe(section);
    document.addEventListener("visibilitychange", sync);
    window.addEventListener("resize", measure);
    motionQuery.addEventListener("change", sync);
    initialFrame = window.requestAnimationFrame(measure);

    return () => {
      if (initialFrame !== null) window.cancelAnimationFrame(initialFrame);
      observer.disconnect();
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("resize", measure);
      motionQuery.removeEventListener("change", sync);
    };
  }, [sectionRef]);

  return { ...state, canAnimate: state.intersecting && state.visible && !state.reduced };
}

const H8_CX_FRAME_CARD_OFFERS = [
  {
    id: "website",
    title: "AI-Ready Website",
    status: "Available service",
    description: "Turn useful website information into clearer questions and next steps.",
    cta: "Explore AI-Ready Website",
    href: S.services,
  },
  {
    id: "cx-ai",
    title: "NEXT CX AI",
    status: "Available product",
    description: "Guide customer conversations and hand over clear context when people need to step in.",
    cta: "Explore NEXT CX AI",
    href: S.products,
  },
  {
    id: "smartpost",
    title: "SmartPost AI",
    status: "Beta · Pilot available",
    description: "Plan, draft and review content with human approval before publishing.",
    cta: "Explore SmartPost AI",
    href: S.products,
  },
];

const H8_CX_FRAME_TOTAL_MS = 12000;

function useH8CXFrameSequence(canAnimate, reduced) {
  const [frame, setFrame] = useState_H8CX(reduced ? 2 : 0);
  const elapsedRef = useRef_H8CX(reduced ? H8_CX_FRAME_TOTAL_MS : 0);
  const lastTimeRef = useRef_H8CX(null);
  const animationRef = useRef_H8CX(null);

  useEffect_H8CX(() => {
    if (reduced) {
      elapsedRef.current = H8_CX_FRAME_TOTAL_MS;
      lastTimeRef.current = null;
      setFrame(2);
      return undefined;
    }
    if (!canAnimate || elapsedRef.current >= H8_CX_FRAME_TOTAL_MS) {
      lastTimeRef.current = null;
      return undefined;
    }
    const tick = (now) => {
      if (lastTimeRef.current === null) lastTimeRef.current = now;
      elapsedRef.current = Math.min(H8_CX_FRAME_TOTAL_MS, elapsedRef.current + Math.min(64, now - lastTimeRef.current));
      lastTimeRef.current = now;
      const nextFrame = elapsedRef.current < 3500 ? 0 : elapsedRef.current < 7000 ? 1 : 2;
      setFrame((current) => (current === nextFrame ? current : nextFrame));
      if (elapsedRef.current < H8_CX_FRAME_TOTAL_MS) animationRef.current = window.requestAnimationFrame(tick);
    };
    animationRef.current = window.requestAnimationFrame(tick);
    return () => {
      if (animationRef.current !== null) window.cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
      lastTimeRef.current = null;
    };
  }, [canAnimate, reduced]);

  return frame;
}

function H8CXCardIcon({ offerId }) {
  if (offerId === "website") return <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="8" /><path d="M4 12h16M12 4a12 12 0 0 1 0 16M12 4a12 12 0 0 0 0 16" /></svg>;
  if (offerId === "cx-ai") return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 5h14v10H9l-4 4z" /><path d="M9 10h6M12 7v6" /></svg>;
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 4h11l3 3v13H6z" /><path d="M17 4v4h4M9 15l6-6 2 2-6 6H9z" /></svg>;
}

function H8CXFrameCard({ offer, frame }) {
  const imageBase = `/assets/section-05-ai-marketing-cx/v2`;
  const assetName = offer.id === "website" ? "ai-ready-website" : offer.id === "cx-ai" ? "next-cx-ai" : "smartpost-ai";
  return (
    <a className="h8-cx-frame-card" href={offer.href} data-h8-cx-card={offer.id}>
      <div className="h8-cx-frame-card-head">
        <span className="h8-cx-frame-card-icon"><H8CXCardIcon offerId={offer.id} /></span>
        <strong>{offer.title}</strong>
        <span className="h8-cx-frame-status">{offer.status}</span>
        <i aria-hidden="true" />
      </div>
      <div className="h8-cx-frame-media" data-h8-cx-frame={frame + 1}>
        {[0, 1, 2].map((index) => (
          <picture className={`h8-cx-frame-image${index === frame ? " is-active" : ""}${index < frame ? " is-past" : ""}`} key={index} aria-hidden={index === frame ? undefined : "true"}>
            <source media="(max-width: 640px)" srcSet={`${imageBase}/mobile/${assetName}-frame-0${index + 1}.png`} />
            <img src={`${imageBase}/desktop/${assetName}-frame-0${index + 1}.png`} alt={index === frame ? `Illustrative ${offer.title} workflow, frame ${index + 1} of 3.` : ""} />
          </picture>
        ))}
      </div>
      <span className="h8-cx-frame-disclosure">Illustrative interface</span>
      <span className="h8-cx-frame-description">{offer.description}</span>
      <span className="h8-cx-frame-cta">{offer.cta} <b aria-hidden="true">→</b></span>
    </a>
  );
}

function H8CXFrameCardMotionPilot() {
  const sectionRef = useRef_H8CX(null);
  const lifecycle = useH8CXLifecycle(sectionRef);
  const frame = useH8CXFrameSequence(lifecycle.canAnimate, lifecycle.reduced);
  const journeyStage = frame === 0 ? "attract" : frame === 1 ? "respond" : "handover";

  return (
    <section
      className="section-pad h8-cx-frame-section"
      id="ai-marketing"
      ref={sectionRef}
      data-h8-cx-section="true"
      data-h8-cx-motion-pilot="true"
      data-h8-cx-frame-card-pilot="true"
      data-h8-cx-frame={frame + 1}
      data-h8-cx-motion-active={lifecycle.canAnimate ? "true" : "false"}
      data-h8-cx-reduced-motion={lifecycle.reduced ? "true" : "false"}
      data-h8-cx-journey-stage={journeyStage}
    >
      <style>{`
        .h8-cx-frame-section { position:relative; overflow:clip; background:var(--paper); }
        .h8-cx-frame-section .container { max-width:1380px; }
        .h8-cx-frame-header { display:grid; grid-template-columns:minmax(0,.85fr) minmax(440px,1.15fr); align-items:center; gap:48px; }
        .h8-cx-frame-copy .eyebrow { margin-bottom:24px; color:var(--indigo-deep); }
        .h8-cx-frame-copy h2 { max-width:12ch; margin:0; font-size:clamp(44px,5vw,76px); line-height:.98; letter-spacing:-.067em; }
        .h8-cx-frame-copy h2 em { font-style:normal; color:var(--indigo-deep); }
        .h8-cx-frame-copy .lead { max-width:510px; margin:28px 0 0; color:var(--fg-muted); font-size:clamp(16px,1.5vw,20px); line-height:1.52; }
        .h8-cx-frame-map { position:relative; min-height:390px; overflow:hidden; }
        .h8-cx-frame-map svg { position:absolute; inset:0; width:100%; height:100%; overflow:visible; }
        .h8-cx-frame-map-orbit { fill:none; stroke:color-mix(in oklch,var(--indigo) 74%,#fff); stroke-width:1.5; }
        .h8-cx-frame-map-outer { fill:none; stroke:color-mix(in oklch,var(--fg-subtle) 24%,transparent); stroke-width:1; stroke-dasharray:4 8; }
        .h8-cx-frame-map-guide { stroke:color-mix(in oklch,var(--fg-subtle) 25%,transparent); stroke-width:1; stroke-dasharray:4 5; }
        .h8-cx-frame-map-particle { fill:var(--indigo); filter:drop-shadow(0 3px 5px color-mix(in srgb,var(--indigo) 42%,transparent)); }
        .h8-cx-frame-map-core { fill:var(--indigo); } .h8-cx-frame-map-core-ring { fill:none; stroke:color-mix(in oklch,var(--indigo) 25%,transparent); stroke-width:1; }
        .h8-cx-frame-map-node { fill:var(--paper); stroke:var(--indigo); stroke-width:2; } .h8-cx-frame-map-dot { fill:color-mix(in oklch,var(--fg-subtle) 44%,transparent); }
        .h8-cx-frame-map-label { position:absolute; z-index:2; display:flex; align-items:center; gap:12px; color:var(--fg); font-size:clamp(13px,1.5vw,18px); font-weight:650; letter-spacing:-.02em; }
        .h8-cx-frame-map-label span { display:grid; width:46px; height:46px; place-items:center; border:1px solid var(--line); border-radius:15px; background:color-mix(in oklch,var(--paper) 88%,transparent); box-shadow:0 10px 26px color-mix(in srgb,var(--ink) 6%,transparent); }
        .h8-cx-frame-map-label svg { position:static; width:22px; height:22px; fill:none; stroke:currentColor; stroke-width:1.45; }
        .h8-cx-frame-map-label.attract { top:1%; right:15%; } .h8-cx-frame-map-label.understand { top:45%; right:0; } .h8-cx-frame-map-label.respond { right:8%; bottom:5%; } .h8-cx-frame-map-label.handover { left:0; bottom:8%; } .h8-cx-frame-map-label.learn { left:2%; top:47%; }
        .h8-cx-frame-map-label.is-active { color:var(--indigo-deep); } .h8-cx-frame-map-label.is-active span { border-color:color-mix(in oklch,var(--indigo) 45%,var(--line)); box-shadow:0 0 0 5px color-mix(in oklch,var(--indigo) 10%,transparent); }
        .h8-cx-frame-section[data-h8-cx-motion-active="true"] .h8-cx-frame-map-particle { animation:h8-cx-map-path 7s ease-in-out both; }
        .h8-cx-frame-section[data-h8-cx-motion-active="true"][data-h8-cx-frame="2"] .h8-cx-frame-map-particle { animation-delay:-3.5s; }
        .h8-cx-frame-section[data-h8-cx-motion-active="true"][data-h8-cx-frame="3"] .h8-cx-frame-map-particle { animation-delay:-7s; }
        @keyframes h8-cx-map-path { 0% { transform:translate(0,0); opacity:.35; } 48% { transform:translate(125px,-42px); opacity:1; } 100% { transform:translate(246px,45px); opacity:1; } }
        .h8-cx-frame-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:22px; margin-top:52px; }
        .h8-cx-frame-card { display:flex; min-width:0; flex-direction:column; padding:14px; border:1px solid var(--line); border-radius:22px; background:color-mix(in oklch,var(--paper) 92%,var(--paper-2)); color:var(--fg); text-decoration:none; box-shadow:0 14px 34px color-mix(in srgb,var(--ink) 6%,transparent); transition:transform 320ms var(--ease-rain),border-color 320ms var(--ease-rain),box-shadow 320ms var(--ease-rain); }
        .h8-cx-frame-card:hover { transform:translateY(-4px); border-color:color-mix(in oklch,var(--indigo) 38%,var(--line)); box-shadow:0 20px 42px color-mix(in srgb,var(--ink) 10%,transparent); }
        .h8-cx-frame-card:focus-visible { outline:3px solid var(--indigo); outline-offset:4px; }
        .h8-cx-frame-card-head { display:grid; grid-template-columns:36px minmax(0,1fr) auto 8px; align-items:center; gap:10px; min-height:42px; padding:0 5px 12px; }
        .h8-cx-frame-card-icon { display:grid; width:34px; height:34px; place-items:center; border:1px solid var(--line); border-radius:11px; color:var(--indigo); } .h8-cx-frame-card-icon svg { width:18px; height:18px; fill:none; stroke:currentColor; stroke-width:1.5; }
        .h8-cx-frame-card-head strong { font-size:15px; line-height:1.2; letter-spacing:-.025em; } .h8-cx-frame-status { color:var(--fg-muted); font-size:10px; white-space:nowrap; } .h8-cx-frame-card-head i { width:7px; height:7px; border-radius:999px; background:var(--indigo); }
        .h8-cx-frame-media { position:relative; aspect-ratio:3 / 2; overflow:hidden; border-radius:15px; background:var(--ink); }
        .h8-cx-frame-image { position:absolute; inset:0; display:block; opacity:0; transform:translateX(20px); transition:opacity 600ms ease-out,transform 600ms ease-out; } .h8-cx-frame-image.is-active { z-index:1; opacity:1; transform:translateX(0); } .h8-cx-frame-image.is-past { transform:translateX(-20px); }
        .h8-cx-frame-image img { display:block; width:100%; height:100%; object-fit:contain; }
        .h8-cx-frame-disclosure { display:block; margin-top:11px; color:var(--fg-subtle); font-size:10px; }
        .h8-cx-frame-description { display:block; min-height:3.2em; margin-top:16px; color:var(--fg-muted); font-size:14px; line-height:1.5; }
        .h8-cx-frame-cta { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-top:15px; color:var(--indigo-deep); font-size:13px; font-weight:700; } .h8-cx-frame-cta b { font-size:18px; font-weight:400; transition:transform 220ms var(--ease-rain); } .h8-cx-frame-card:hover .h8-cx-frame-cta b { transform:translateX(4px); }
        .h8-cx-frame-section[data-h8-cx-reduced-motion="true"] *, .h8-cx-frame-section[data-h8-cx-motion-active="false"] .h8-cx-frame-map-particle { animation:none !important; }
        @media (max-width:980px) { .h8-cx-frame-header { grid-template-columns:1fr; gap:26px; } .h8-cx-frame-copy h2 { max-width:13ch; } .h8-cx-frame-map { min-height:360px; } .h8-cx-frame-grid { gap:14px; margin-top:36px; } .h8-cx-frame-card-head { grid-template-columns:32px minmax(0,1fr) 7px; } .h8-cx-frame-card-icon { width:30px; height:30px; } .h8-cx-frame-status { grid-column:2; grid-row:2; } .h8-cx-frame-card-head i { grid-column:3; grid-row:1; } }
        @media (max-width:640px) { .h8-cx-frame-copy .eyebrow { margin-bottom:17px; } .h8-cx-frame-copy h2 { max-width:none; font-size:clamp(34px,9.7vw,43px); } .h8-cx-frame-copy h2 em { white-space:nowrap; } .h8-cx-frame-copy .lead { margin-top:22px; font-size:16px; } .h8-cx-frame-map { min-height:285px; margin:0 -4px; } .h8-cx-frame-map-label { gap:7px; font-size:11px; } .h8-cx-frame-map-label span { width:32px; height:32px; border-radius:10px; } .h8-cx-frame-map-label svg { width:16px; height:16px; } .h8-cx-frame-map-label.attract { top:0; right:7%; } .h8-cx-frame-map-label.understand { top:45%; right:-2%; } .h8-cx-frame-map-label.respond { right:2%; bottom:2%; } .h8-cx-frame-map-label.handover { left:0; bottom:4%; } .h8-cx-frame-map-label.learn { left:0; top:47%; } .h8-cx-frame-grid { grid-template-columns:1fr; gap:16px; margin-top:29px; } .h8-cx-frame-card { padding:10px; border-radius:18px; } .h8-cx-frame-card-head { grid-template-columns:36px minmax(0,1fr) auto 7px; min-height:48px; padding:2px 4px 10px; } .h8-cx-frame-status { grid-column:3; grid-row:1; font-size:9px; } .h8-cx-frame-card-head i { grid-column:4; grid-row:1; } .h8-cx-frame-card-icon { width:32px; height:32px; } .h8-cx-frame-media { aspect-ratio:1920 / 496; border-radius:12px; } .h8-cx-frame-description { min-height:0; margin:13px 4px 0; font-size:14px; } .h8-cx-frame-disclosure { margin:9px 4px 0; } .h8-cx-frame-cta { min-height:48px; margin:5px 4px 0; } }
      `}</style>
      <div className="container">
        <div className="h8-cx-frame-header">
          <div className="h8-cx-frame-copy">
            <div className="eyebrow">AI MARKETING &amp; CUSTOMER EXPERIENCE</div>
            <h2>Turn customer attention into <em>clearer conversations</em> and <em>next steps.</em></h2>
            <p className="lead">From discovery to human follow-up, we design customer experiences that help people find useful information, clarify what they need, receive an approved response, and move forward.</p>
          </div>
          <div className="h8-cx-frame-map" aria-label="Customer journey: Attract, Understand, Respond, Human handover, Learn">
            <svg viewBox="0 0 600 390" role="img" aria-hidden="true">
              <ellipse className="h8-cx-frame-map-outer" cx="310" cy="202" rx="235" ry="145" />
              <ellipse className="h8-cx-frame-map-orbit" cx="308" cy="198" rx="212" ry="118" transform="rotate(-4 308 198)" />
              <path className="h8-cx-frame-map-guide" d="M102 198h74M230 96l30 60M401 128l-31 53M453 258l-68-30M220 292l38-54" />
              <circle className="h8-cx-frame-map-node" cx="102" cy="198" r="7" /><circle className="h8-cx-frame-map-node" cx="260" cy="84" r="7" /><circle className="h8-cx-frame-map-node" cx="441" cy="158" r="7" /><circle className="h8-cx-frame-map-node" cx="430" cy="274" r="7" /><circle className="h8-cx-frame-map-node" cx="214" cy="288" r="7" />
              <circle className="h8-cx-frame-map-core-ring" cx="308" cy="198" r="46" /><circle className="h8-cx-frame-map-core-ring" cx="308" cy="198" r="27" /><circle className="h8-cx-frame-map-core" cx="308" cy="198" r="7" />
              <circle className="h8-cx-frame-map-dot" cx="270" cy="162" r="3" /><circle className="h8-cx-frame-map-dot" cx="350" cy="176" r="3" /><circle className="h8-cx-frame-map-dot" cx="328" cy="238" r="3" />
              <circle className="h8-cx-frame-map-particle" cx="102" cy="198" r="7" />
            </svg>
            {H8_CX_JOURNEY.map((stage) => <div key={stage.id} className={`h8-cx-frame-map-label ${stage.id}${stage.id === journeyStage ? " is-active" : ""}`}><span><H8CXCardIcon offerId={stage.id === "respond" ? "cx-ai" : stage.id === "attract" ? "website" : "smartpost"} /></span>{stage.label}</div>)}
          </div>
        </div>
        <div className="h8-cx-frame-grid">
          {H8_CX_FRAME_CARD_OFFERS.map((offer) => <H8CXFrameCard key={offer.id} offer={offer} frame={frame} />)}
        </div>
      </div>
    </section>
  );
}

function AIMarketingCX() {
  return <H8CXFrameCardMotionPilot />;
}

export { AIMarketingCX };
